import { post } from './api';
import { getUserType, getIsCompany, userTypes } from './userTypes';

const sharetribeSdk = require('sharetribe-flex-sdk');

const sdk = sharetribeSdk.createInstance({
    clientId: process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID,
});

export const subscriptionTypes = {
    basic: 'basic',
    midTier: 'midTier',
    enterprise: 'enterprise'
}

export const UIControls = {
    verifyButton: "verifyButton",
    addListingButton: "addListingButton",
}


export const getUserSubscription = (currentUser) => {
    if (!currentUser) {
        return null;
    }

    // Safely check if privateData and subscriptions exist
    const privateData = currentUser.attributes?.profile?.privateData;
    if (!privateData || !privateData.subscriptions) {
        return null;
    }
    
    if (privateData.subscriptions.length === 0) {
        return null;
    }
    else {
        return privateData.subscriptions[0].subscriptionType;
    }

    //TODO check with that convention like privateseller_basic
    
    //return currentUser.attributes.profile.privateData.subscriptions?
    //.find(s => s.userType === getUserType(currentUser))?
    //.subscriptionType; //TODO see if multiple subscriptions or just one
}


// Number of days before trial expiry at which we start nudging the user to subscribe.
export const TRIAL_BANNER_THRESHOLD_DAYS = 5;

/**
 * Decide whether to show a subscription nudge banner for a company/broker user
 * who is on (or has just finished) a Telia trial and has no active subscription.
 *
 * @param {Object} currentUser API entity
 * @returns {null | { kind: 'expiringSoon', daysRemaining: number } | { kind: 'expired' }}
 */
export const getTrialBannerState = (currentUser) => {
    if (!currentUser) {
        return null;
    }

    // Paying subscribers should never be nagged.
    if (getUserSubscription(currentUser)) {
        return null;
    }

    const profile = currentUser.attributes?.profile;
    const protectedData = profile?.protectedData;

    // The trial only applies to company users who were granted one.
    // Must use getIsCompany(): 'broker' is not a Console user type, so the old
    // direct comparison meant real company accounts never saw this banner.
    const isCompanyUser = getIsCompany(currentUser);
    const teliaTrialActive = protectedData?.teliaTrialActive;
    const teliaTrialExpiry = protectedData?.teliaTrialExpiry;

    if (!isCompanyUser || !teliaTrialActive || !teliaTrialExpiry) {
        return null;
    }

    const expiryDate = new Date(teliaTrialExpiry);
    const now = new Date();

    // Note: teliaTrialActive is a stale flag that is never flipped off at expiry,
    // so the date comparison below is what actually determines real status.
    if (now >= expiryDate) {
        return { kind: 'expired' };
    }

    const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= TRIAL_BANNER_THRESHOLD_DAYS) {
        return { kind: 'expiringSoon', daysRemaining };
    }

    return null;
};

export const getSubscriptionDetails = (priceId, subscriptionPrices) => {
    const subscriptionForPriceId = subscriptionPrices?.find(s => s.id === priceId);
    if (!subscriptionForPriceId || !subscriptionForPriceId.lookup_key) {
        return {
            priceId,
            subscriptionType: subscriptionTypes.basic,
        };
    }

    const lookup = subscriptionForPriceId.lookup_key.split("_");

    return { 
        priceId: priceId, 
        //userType: lookup[0], // TODO later 
        subscriptionType: lookup[1] 
    }
}

export const getSubscriptionPermissions = (userType, subscriptionType, uiOption) => {
    if ((subscriptionType === subscriptionTypes.choice ||
        subscriptionType === subscriptionTypes.premium ||
        subscriptionType === subscriptionTypes.value) &&
        uiOption === UIControls.verifyButton

    ) {
        return true;
    } else if ((subscriptionType === subscriptionTypes.choice ||
        subscriptionType === subscriptionTypes.premium ||
        subscriptionType === subscriptionTypes.value) &&
        uiOption === UIControls.addListingButton
    ) {
        return true;
    }

    if ((subscriptionType === subscriptionTypes.choice ||
        subscriptionType === subscriptionTypes.premium ||
        subscriptionType === subscriptionTypes.value) &&
        (userType === userTypes.housesitter
            || userType === userTypes.homeownerAndHousesitter
        ) &&
        uiOption === UIControls.findAHouseSitButton
    ) {
        return true;
    }

    return false;
}

export const getDescriptionCharactersLimit = (currentUser) => {
    const userType = getUserType(currentUser);
    const subscriptionType = getUserSubscription(currentUser);
    if (userType === userTypes.homeowner && subscriptionType === subscriptionTypes.value) {
        return 150;
    }
    if (userType === userTypes.homeowner && subscriptionType === subscriptionTypes.choice) {
        return 150;
    }

    if (userType === userTypes.homeowner && subscriptionType === subscriptionTypes.premium) {
        return 800;
    }

    if (userType === userTypes.housesitter && subscriptionType === subscriptionTypes.value) {
        return 150;
    }
    if (userType === userTypes.housesitter && subscriptionType === subscriptionTypes.choice) {
        return 150;
    }

    if (userType === userTypes.housesitter && subscriptionType === subscriptionTypes.premium) {
        return 800;
    }
}

export const getListingPhotosLimit = (currentUser) => {
    const userType = getUserType(currentUser);
    const subscriptionType = getUserSubscription(currentUser);

    if (userType === userTypes.homeowner && subscriptionType === subscriptionTypes.value) {
        return 5;
    }
    if (userType === userTypes.homeowner && subscriptionType === subscriptionTypes.choice) {
        return 10;
    }

    if (userType === userTypes.homeowner && subscriptionType === subscriptionTypes.premium) {
        return 20;
    }

    if (userType === userTypes.housesitter && subscriptionType === subscriptionTypes.value) {
        return 5;
    }
    if (userType === userTypes.housesitter && subscriptionType === subscriptionTypes.choice) {
        return 10;
    }

    if (userType === userTypes.housesitter && subscriptionType === subscriptionTypes.premium) {
        return 20;
    }
}


export const getSubscriptions = (setIsStripeConnected, setSubscriptions) => {
    sdk.currentUser
        .show({
            include: ['stripeAccount', 'stripeCustomer'],
        })
        .then(resp => {
            const isConnected = resp.data.data.attributes.stripeConnected;
            const userEmail = resp.data.data.attributes.email;
            const userId = resp.data.data.id.uuid;
            const hasCustomerId = resp.data.data.attributes.profile.privateData
                ? resp.data.data.attributes.profile.privateData.stripeCustomerId
                    ? resp.data.data.attributes.profile.privateData.stripeCustomerId
                    : null
                : null;
            setIsStripeConnected(isConnected);
            return [hasCustomerId, userEmail, userId];
        })
        .then(resp => {
            const stripeCustomerId = resp[0];
            const userEmail = resp[1];
            const userId = resp[2];

            if (stripeCustomerId) {
                const requestOptions = {
                    stripeCustomerId: stripeCustomerId,
                };

                const fetchData = async () => {
                    const { subscriptions } = await post('/api/subscriptions-list', requestOptions);

                    setSubscriptions(subscriptions.data);
                };
                fetchData();
            } else {
                post('/api/create-customer', {
                    email: userEmail,
                    userId: userId,
                }).catch(error => console.log(error));

                setSubscriptions([]);
            }
        })
        .catch(e => console.log(e));
};